import React, { useEffect, useState } from 'react';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ColDef, GridReadyEvent, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import { DataTable } from '@fluentsms/agentnet-web-components';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { SearchFormTemplatesResponseItem } from 'api/formManagement/types';
import { FORM_TYPE_OPTIONS, UNDERWRITER_OPTIONS, US_STATE_OPTIONS } from './constants';

export interface SearchFormValues {
  formItemNo: string;
  formType: string;
  underwriter: string;
  state: string;
}

interface SearchFormFieldsProps {
  onSearch: (values: SearchFormValues) => void;
  onClearSearch: () => void;
  searchResults: SearchFormTemplatesResponseItem[];
  isSearching: boolean;
  onEditClick: (row: SearchFormTemplatesResponseItem) => void;
  formValues: SearchFormValues;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    noRows: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 220,
      justifyContent: 'center',
    },
  }),
);

const SearchFormFields: React.FC<SearchFormFieldsProps> = ({
  onSearch,
  onClearSearch,
  searchResults,
  isSearching,
  onEditClick,
  formValues,
}) => {
  const classes = useStyles();

  const [formItemNo, setFormItemNo] = useState('');
  const [formType, setFormType] = useState('');
  const [underwriter, setUnderwriter] = useState('');
  const [state, setState] = useState('');

  // Sync form values from parent component
  useEffect(() => {
    setFormItemNo(formValues.formItemNo);
    setFormType(formValues.formType);
    setUnderwriter(formValues.underwriter);
    setState(formValues.state);
  }, [formValues]);

  const handleSearch = () => {
    onSearch({ formItemNo, formType, underwriter, state });
  };

  const handleClearSearch = () => {
    setFormItemNo('');
    setFormType('');
    setUnderwriter('');
    setState('');
    onClearSearch();
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Form/Item No.',
      field: 'starsItemNumber',
      filter: 'agTextColumnFilter',
      minWidth: 130,
      maxWidth: 256,
      tooltipField: 'starsItemNumber',
    },
    {
      headerName: 'Form Type',
      field: 'starsItemType',
      filter: 'agTextColumnFilter',
      minWidth: 130,
      maxWidth: 256,
      tooltipField: 'starsItemType',
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === 'POLICY') return 'JACKET';
        if (params.value === 'Policy') return 'Jacket';
        return params.value;
      },
    },
    {
      headerName: 'Description',
      field: 'formDescription',
      filter: 'agTextColumnFilter',
      minWidth: 200,
      tooltipField: 'formDescription',
    },
    {
      headerName: 'Underwriter',
      field: 'underwriterCode',
      filter: 'agTextColumnFilter',
      minWidth: 130,
      maxWidth: 200,
      tooltipField: 'underwriterCode',
    },
    {
      headerName: 'State',
      field: 'stateCode',
      filter: 'agTextColumnFilter',
      minWidth: 100,
      maxWidth: 150,
      tooltipField: 'stateCode',
    },
    {
      headerName: '',
      field: 'edit',
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' },
      cellRenderer: (params: ICellRendererParams) => (
        <Tooltip title="Edit Form">
          <EditOutlinedIcon
            fontSize="small"
            htmlColor="#0074CA"
            style={{ cursor: 'pointer' }}
            onClick={() => onEditClick(params.data)}
            data-testid={`SearchForm-EditForm-${params.data?.starsItemNumber}`}
          />
        </Tooltip>
      ),
      maxWidth: 50,
    },
  ];

  const agGridConfig = (): AgGridReactProps => {
    return {
      rowData: searchResults ?? [],
      columnDefs: columnDefs,
      defaultColDef: {
        editable: false,
        filter: 'agTextColumnFilter',
        flex: 1,
        floatingFilter: true,
        resizable: true,
        sortable: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: false,
        width: 144,
        minWidth: 120,
      },
      onGridReady: (event: GridReadyEvent) => {
        event.api.sizeColumnsToFit();
      },
    };
  };

  return (
    <Box className={classes.container}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={3}>
          <AgentNetTextInput
            fullWidth
            label="Form/Item No."
            name="formItemNo"
            value={formItemNo}
            onChange={(e) => setFormItemNo(e.target.value)}
            data-testid="SearchForm-SearchField-FormItemNo"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AgentNetDropdownSelector
            fullWidth
            label="Form Type"
            name="formType"
            value={formType}
            options={FORM_TYPE_OPTIONS}
            menuOption={(value: string) => setFormType(value)}
            data-testid="SearchForm-SearchField-FormType"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AgentNetDropdownSelector
            fullWidth
            label="Underwriter"
            name="underwriter"
            value={underwriter}
            options={UNDERWRITER_OPTIONS}
            menuOption={(value: string) => setUnderwriter(value)}
            data-testid="SearchForm-SearchField-Underwriter"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AgentNetDropdownSelector
            fullWidth
            label="State"
            name="state"
            value={state}
            options={US_STATE_OPTIONS}
            menuOption={(value: string) => setState(value)}
            data-testid="SearchForm-SearchField-State"
          />
        </Grid>
      </Grid>
      <Box className={classes.buttonRow}>
        <AgentNetButton
          color="primary"
          data-testid="SearchForm-Clear-Button"
          onClick={handleClearSearch}
          variant="outlined"
          size="small"
        >
          Clear Search
        </AgentNetButton>
        <AgentNetButton
          color="primary"
          data-testid="SearchForm-Search-Button"
          variant="contained"
          onClick={handleSearch}
          size="small"
          disabled={isSearching}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </AgentNetButton>
      </Box>
      <Grid container spacing={2} style={{ marginTop: 16 }}>
        <Grid item xs={12} data-testid="SearchForm-Grid">
          {isSearching ? (
            <div className={classes.noRows}>
              <LoadingSpinner status="pending" className="files-container--spinner-root" />
            </div>
          ) : (
            <div style={{ height: 600, width: '100%' }}>
              <DataTable {...agGridConfig()} fixedHeader fixedBottomScroll />
            </div>
          )}
          {searchResults && (
            <Typography variant="body2">
              Showing {searchResults.length} of {searchResults.length} forms
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchFormFields;
